import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import {
    APIS,
    SF_BIDR_USR_INFO,
    SG_BIDR_USR_CTGR_LIST,
    URLS,
} from "@/tra/tancis/ptli/usr/bidrusr/services/PtliBidrUsrService";

import { getNameOfJSDocTypedef } from "typescript";

/*
 * Bidder Approval Request Details
 * !== 공매자 승인 요청 상세 ==!
 */
export const PTLI0911003S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const { xtrnUsrUuid } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const [rtrnYn, setRtrnYn] = useState("N");
    const [frstUsrStatCd, setFrstUsrStatCd] = useState(); // Save status information first selected
    let isFrst = true;

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "COM0021" },
            { area: "comnCd", comnCd: "PTL0001" },
            { area: "cstmCd" },
            { area: "cntyCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Bidder User Information !== 사용자(대표자) 정보 ==!
        bidrUsrInfo: useForm({
            defaultSchema: SF_BIDR_USR_INFO,
        }),
    };

    const grid = {
        bidrUsrCtgrList: useGrid({ defaultSchema: SG_BIDR_USR_CTGR_LIST }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get License User Information !== 면허 사용자 정보 조회 ==!
        getBidrUsrInfo: useFetch({
            api: () => APIS.getBidrUsrInfo(xtrnUsrUuid),
            enabled: !!xtrnUsrUuid,
            onSuccess: (data) => {
                const bidrUsrInfo = data.bidrUsrInfo.content;
                const bidrUsrCtgrList = bidrUsrInfo.bidrUsrCtgrList;

                bidrUsrCtgrList.forEach((element: any) => {
                    element.prevActnCtgrCd = element.actnCtgrCd;
                });

                form.bidrUsrInfo.setValues(bidrUsrInfo);
                grid.bidrUsrCtgrList.setData(comnUtils.getGridData(bidrUsrCtgrList));

                if (isFrst) {
                    isFrst = false;
                    setFrstUsrStatCd(bidrUsrInfo.usrStatCd);
                }
            },
            onError: (error) => {},
            showToast: true,
        }),

        // Delete Bidder User Information !== 공매 사용자 정보 삭제 ==!
        deleteBidrUsrInfo: useFetch({
            api: (xtrnUsrUuid) => APIS.deleteBidrUsrInfo(xtrnUsrUuid),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onCancel: () => {
                        navigate(`${URLS.ptli0911001q}?menuId=${menuId}`);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),

        // Update Bidder User Information !== 기관 사용자 정보 수정 ==!
        updateBidrUsrInfo: useFetch({
            api: (info: any) => APIS.updateBidrUsrInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00059", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        navigate(`${URLS.ptli0911001q}?menuId=${menuId}`);
                    },
                    close: false,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00022" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),

        // Reset the Bidder User password. !== 공매사용자 비밀번호를 초기화한다. ==!
        updateBidrUsrPwdInit: useFetch({
            api: (data) => APIS.updateBidrUsrPwdInit(data),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.ptl.00764", // A temporary password has been sent. !==  임시 비밀번호가 발송되었습니다. ==!
                });
            },
            onError: (error) => {
                const data = error.response.data;
                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
    };

    const setRequired = (value: Boolean) => {
        form.bidrUsrInfo.setSchemas(
            [
                "xtrnUsrIdntfNo",
                "usrNm",
                value ? "usrStatCd" : "cstmOfcrNo",
                "frgnrYn",
                "aplcCntyCd",
                "usrMoblNo",
                "usrEmlAddr",
                "rsdtAddr",
            ],
            { required: value },
        );
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Delete Bidder User Information !== 공매 사용자 정보 삭제 ==!
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    fetch.deleteBidrUsrInfo.fetch(xtrnUsrUuid);
                },
            });
        },

        // Click Update Button !== 수정 버튼 클릭 ==!
        onUpdateBtnClick: () => {
            debugger;

            const arrBidrUsrCtgr = grid.bidrUsrCtgrList.getData();
            const arrRow: String[] = [];

            let isCstmOfcr = false;
            // Check for duplicate Code !== 중복 코드 검사 ==!
            arrBidrUsrCtgr.forEach((objPrmt) => {
                if (!comnUtils.isEmpty(objPrmt.actnCtgrCd)) {
                    arrRow.push(objPrmt.actnCtgrCd);

                    if (objPrmt.actnCtgrCd == 1) isCstmOfcr = true;
                }
            });

            const setRow = new Set(arrRow);
            // if (setRow.size < arrRow.length) {
            //     toast.showToast({ type: "warning", content: "msg.00005" }); // Duplicate data exists.., !== 중복된 데이터가 존재합니다.. ==!
            //     return;
            // }

            // Mandatory field skip when changing bidder status !== Bidder 상태 변경시 필수입력 생략 ==!
            const curUsrStatCd = form.bidrUsrInfo.getValue("usrStatCd");
            if (frstUsrStatCd !== curUsrStatCd) {
                form.bidrUsrInfo.clearErrors();

                if (curUsrStatCd == 2) {
                    // Modification Reason is a mandatory field for stauts Revoked
                    if (comnUtils.isEmpty(form.bidrUsrInfo.getValue("mdfyRsn"))) {
                        form.bidrUsrInfo.setError("mdfyRsn", {
                            message: "msg.com.00005", // Mandatory fields !== 필수 입력 필드 ==!
                            type: "error",
                        });
                        return;
                    }
                }

                modal.openModal({
                    content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
                    onConfirm: () => {
                        setRequired(false);

                        // save Information of Organization !== 기관 정보 저장 ==!
                        form.bidrUsrInfo.handleSubmit(
                            () => {
                                const data = form.bidrUsrInfo.getValues();
                                const bidrUsrCtgrList = grid.bidrUsrCtgrList.getData();

                                data.bidrUsrCtgrList = bidrUsrCtgrList;

                                fetch.updateBidrUsrInfo.fetch(data);
                            },
                            () => {
                                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                            },
                        )();
                    },
                });
            } else {
                setRequired(true);

                if (!comnUtils.isEmpty(form.bidrUsrInfo.errors)) {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                    return;
                }

                if (isCstmOfcr && comnUtils.isEmpty(form.bidrUsrInfo.getValue("cstmOfcrNo"))) {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                    return;
                }

                // save Information of Organization !== 기관 정보 저장 ==!
                form.bidrUsrInfo.handleSubmit(
                    () => {
                        const data = form.bidrUsrInfo.getValues();
                        const bidrUsrCtgrList = grid.bidrUsrCtgrList.getData();

                        data.bidrUsrCtgrList = bidrUsrCtgrList;

                        modal.openModal({
                            content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
                            onConfirm: () => {
                                fetch.updateBidrUsrInfo.fetch(data);
                            },
                        });
                    },
                    () => {
                        toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                    },
                )();
            }
        },

        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.bidrUsrCtgrList.getChecked())) {
                modal.openModal({ content: "msg.com.00086" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.com.00049",
                onConfirm: () => {
                    grid.bidrUsrCtgrList.deleteRow("checkbox");
                },
            });
        },

        onResetBtnClick: () => {
            modal.openModal({
                content: "msg.ptl.00749", // Do you want to reset your password? !== 비밀번호를 초기화 하시겠습니까? ==!
                onConfirm: () => {
                    if (!comnUtils.isEmpty(form.bidrUsrInfo.errors)) {
                        toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        return;
                    }
                    form.bidrUsrInfo.handleSubmit(
                        () => {
                            const usrAplyInfo = form.bidrUsrInfo.getValues();

                            fetch.updateBidrUsrPwdInit.fetch(usrAplyInfo);
                        },
                        () => {
                            toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        },
                    )();
                },
            });
        },
        onUsrStatChange: (value: any) => {
            form.bidrUsrInfo.clearErrors();
            if (value == 2) {
                form.bidrUsrInfo.setSchema("mdfyRsn", { required: true });
            } else {
                form.bidrUsrInfo.setSchema("mdfyRsn", { required: false });
            }
        },
    };

    useEffect(() => {
        fetch.getBidrUsrInfo.fetch();
        // fetch.getCoTpAplyList.fetch();
        form.bidrUsrInfo.setEditable("xtrnUsrIdntfNo", false);
        form.bidrUsrInfo.setEditable("usrNm", false);
        form.bidrUsrInfo.setEditable("actnCtgrCd", false);
        form.bidrUsrInfo.setEditable("cstmOfcrNo", false);
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_BIDR_INFO")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.xtrnUsrIdntfNo} />
                                <Group.Control {...form.bidrUsrInfo.schema.usrNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.cstmOfcrNo}></Group.Control>
                                <Group.Control
                                    {...form.bidrUsrInfo.schema.usrStatCd}
                                    onChange={handler.onUsrStatChange}
                                ></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.frgnrYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.aplcCntyCd}></Group.Control>
                                <Group.Control {...form.bidrUsrInfo.schema.usrMoblNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.usrEmlAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.rsdtAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.bidrUsrInfo.schema.mdfyRsn}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
                <Group>
                    <Group.Body>
                        <Group.Section gap={2}>
                            <Layout.Right>
                                <Button
                                    role="gridAdd"
                                    onClick={() => {
                                        grid.bidrUsrCtgrList.addRow();
                                    }}
                                ></Button>
                                <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                            </Layout.Right>
                            <Grid {...grid.bidrUsrCtgrList.grid} />
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0911001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        color="warning"
                        onClick={() => {
                            handler.onResetBtnClick();
                        }}
                    >
                        {t("B_RESET_PWD")}
                    </Button>
                    <Button
                        role="delete"
                        onClick={() => {
                            handler.onDeleteBtnClick();
                        }}
                    ></Button>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.onUpdateBtnClick();
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
